/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { Form } from '../../molecules/PatternForm'
import StatusToast from 'components/molecules/StatusToast/StatusToast'
import { getAllChutes, getAllRules } from '../../../services/templateService'
import { Chute } from 'interfaces/templates'
import { useNavigate } from 'react-router-dom'
import { useOperationModes } from 'core/hooks/useOperationModes'
import { OperationMode } from 'core/domain/operationMode'
import { useSaveTemplate } from '../../../templates/presentation/hooks/useSaveTemplate'
import { Template } from '../../../templates/domain/entities/Template'

interface Rules {
  dndId: string;
  group_id?: number;
  chutes: number[];
  maxCapacity?: number | null;
  provider?: { name: string, code: string } | null;
  providerService?: { name: string, code: string } | null;
}

export default function PatternCreate() {
  const navigate = useNavigate();
  const { saveTemplate, loading: isSaving } = useSaveTemplate();

  const [groups, setGroups] = useState<Rules[]>([{ dndId: `new-${Date.now()}`, chutes: [], maxCapacity: null, provider: null, providerService: null }]);
  const [ruleOptions, setRuleOptions] = useState<{ value: string; label: string }[]>([]);
  const [baseChutes, setBaseChutes] = useState<Chute[]>([]);
  const [patternName, setPatternName] = useState('');
  const [toast, setToast] = useState<{ title: string; type: 'success' | 'error' | 'loading'; visible: boolean }>({
    title: '',
    type: 'success',
    visible: false,
  });

  // Modo de operación
  const { operationModes, requiresChutesConfiguration, findById } = useOperationModes();
  const [selectedOperationMode, setSelectedOperationMode] = useState<OperationMode | null>(null);

  // ── DnD sensors ──────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setGroups(prev => {
        const oldIndex = prev.findIndex(g => g.dndId === active.id);
        const newIndex = prev.findIndex(g => g.dndId === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleOperationModeChange = (code: string, id: number) => {
    const mode = findById(id);
    setSelectedOperationMode(mode);
  };

  const showToast = (title: string, type: 'success' | 'error' | 'loading') => {
    setToast({ title, type, visible: true });
    setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 3000);
  };

  const handleAddRule = () => {
    setGroups(prev => [...prev, { dndId: `new-${Date.now()}`, chutes: [], maxCapacity: null, provider: null, providerService: null }]);
  };

  const handleChutesSelected = (ruleIndex: number, selectedChuteNumbers: number[]) => {
    setGroups(prev => {
      const updated = [...prev];
      const selectedChuteIds = selectedChuteNumbers
        .map(num => baseChutes.find(c => c.chute === num)?.id)
        .filter((id): id is number => id !== undefined);
      updated[ruleIndex].chutes = selectedChuteIds;
      return updated;
    });
  };

  const handleDeleteRule = (dndId: string) => {
    setGroups(prev => prev.filter(g => g.dndId !== dndId));
  };

  const handleReorder = (fromIndex: number, toIndex: number) => {
    setGroups(prev => arrayMove(prev, fromIndex, toIndex));
  };

  const handleUpdateGroup = (ruleIndex: number, group_id: number, chutes: number[]) => {
    setGroups(prev => {
      const updated = [...prev];
      updated[ruleIndex] = { ...updated[ruleIndex], group_id, chutes };
      return updated;
    });
  };

  const handleUpdateRouteChange = (ruleIndex: number, routeChangeConfig: { provider: { name: string, code: string } | null; providerService: { name: string, code: string } | null }) => {
    setGroups(prev => {
      const updated = [...prev];
      updated[ruleIndex] = {
        ...updated[ruleIndex],
        provider: routeChangeConfig.provider,
        providerService: routeChangeConfig.providerService
      };
      return updated;
    });
  }

  const handleMaxCapacityChange = (ruleIndex: number, value: number | null) => {
    setGroups(prev => {
      const updated = [...prev];
      updated[ruleIndex] = { ...updated[ruleIndex], maxCapacity: value };
      return updated;
    });
  };

  const handleCreate = async () => {
    if (!patternName.trim()) {
      return showToast('El nombre de la plantilla es requerido', 'error');
    }

    if (!selectedOperationMode) {
      return showToast('Debe seleccionar un modo de operación', 'error');
    }

    // if (groups.length === 0 || groups.some(g => g.chutes.length === 0)) {
    if (groups.length === 0) {
      return showToast('Todas las reglas deben tener al menos un chute asignado', 'error');
    }

    try {
      // Build Domain Entity
      const domainTemplate: Template = {
        name: patternName.trim(),
        operationMode: selectedOperationMode,
        rules: groups.map(g => ({
          groupId: g.group_id as number,
          chutes: g.chutes
            .map(id => baseChutes.find(c => c.id === id)?.chute)
            .filter((chute): chute is number => chute !== undefined),
          maxCapacity: g.maxCapacity && g.maxCapacity > 0 ? g.maxCapacity : null,
          provider: g.provider || null,
          providerService: g.providerService || null
        }))
      };
      const savedTemplate = await saveTemplate(domainTemplate);

      showToast('Plantilla creada con éxito', 'success');
      navigate(`/templates/show/${savedTemplate.id}`);
    } catch (error: any) {
      showToast(error?.message || 'Error al crear la plantilla', 'error');
    }
  };

  const buttonValidate = () => {
    if (!patternName.trim() || !selectedOperationMode || groups.length === 0 || isSaving) {
      return true;
    }

    // Si es modo Sorter, validar que cada regla tenga tipo de regla y chutes
    if (requiresChutesConfiguration(selectedOperationMode)) {
      const isAnyRuleInvalid = groups.some(rule => {
        // Validar que tenga tipo de regla seleccionado
        if (!rule.group_id) {
          return true;
        }
        // Validar que tenga al menos un chute
        if (rule.chutes.length === 0) {
          return true;
        }
        // Validar que si tiene provider, también tenga providerService
        if (rule.provider !== null && rule.providerService === null) {
          return true;
        }
        return false;
      });

      return isAnyRuleInvalid;
    }

    // Para modo Classification y otros, validar que tenga tipo de regla y cambio de ruta
    const isAnyRuleInvalid = groups.some(rule => {
      // Validar que tenga tipo de regla seleccionado
      if (!rule.group_id) {
        return true;
      }
      // Validar que si tiene provider, también tenga providerService
      if (rule.provider !== null && rule.providerService === null) {
        return true;
      }
      return false;
    });

    return isAnyRuleInvalid;
  }

  const renderRuleItems = () =>
    groups.map((group, index) => {
      const defaultChutes = group.chutes
        .map(chute => {
          const match = baseChutes.find(c => c.chute === chute);
          return match ? { chute: match.chute, id: match.id } : null;
        })
        .filter((c): c is { chute: number; id: number } => c !== null);

      const disabledRuleIds = groups
        .map(g => g.group_id)
        .filter((gid): gid is number => typeof gid === 'number')
        .filter((gid, i) => i !== index);

      const assignedChutes = groups.flatMap((g, i) => (i === index ? [] : g.chutes));

      return (
        <Form.RuleItem
          key={group.dndId}
          dndId={group.dndId}
          position={index + 1}
          totalCount={groups.length}
          chutes={baseChutes}
          ruleOptions={ruleOptions}
          onDelete={() => handleDeleteRule(group.dndId)}
          onReorder={handleReorder}
          ruleIndex={index}
          onChutesSelected={handleChutesSelected}
          selectedChutes={assignedChutes}
          onGroupUpdate={handleUpdateGroup}
          selectedRuleId={group.group_id?.toString()}
          defaultChutes={defaultChutes}
          disabledRuleIds={disabledRuleIds}
          showChutes={requiresChutesConfiguration(selectedOperationMode)}
          onRouteChangeUpdate={(config) => handleUpdateRouteChange(index, config)}
          maxCapacity={group.maxCapacity}
          onMaxCapacityChange={handleMaxCapacityChange}
        />
      );
    });

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const rulesResponse = await getAllRules();
        const options = rulesResponse?.groups?.map((group: { id: number; name: string }) => ({
          value: group.id.toString(),
          label: group.name,
        })) ?? [];
        setRuleOptions(options);
      } catch (err) {
        setRuleOptions([]);
      }
    };
    fetchRules();
  }, []);

  useEffect(() => {
    if (!selectedOperationMode) return;
    const fetchChutes = async () => {
      try {
        const chutesResponse = await getAllChutes();
        const chutes = chutesResponse.data?.default_chutes || [];
        const transformedChutes: Chute[] = chutes.map((item: any) => ({
          id: item.id,
          chute: item.number,
          type: 'available',
        }));
        setBaseChutes(transformedChutes);
      } catch {
        setBaseChutes([]);
      }
    };
    fetchChutes();
  }, [selectedOperationMode]);

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={groups.map(g => g.dndId)}
          strategy={verticalListSortingStrategy}
        >
          <Form
            onAddRule={handleAddRule}
            onCreateTemplate={handleCreate}
            onNameChange={val => setPatternName(val)}
            templateName={patternName}
            buttonDisabled={buttonValidate()}
            operationModes={operationModes}
            selectedOperationMode={selectedOperationMode?.code || ''}
            onOperationModeChange={handleOperationModeChange}
          >
            {renderRuleItems()}
          </Form>
        </SortableContext>
      </DndContext>

      {toast.visible && <StatusToast title={toast.title} type={toast.type} />}
    </>
  );
}